import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Pagination,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface City {
  citiesId: number;
  name: string;
}
interface Manager {
  managerId: number;
  name: string;
}
interface Shop {
  shopId: number;
  name: string;
  address: string;
  workTime?: string;
  city?: City | null;
  manager?: Manager | null;
}

export default function Shops() {
  const navigate = useNavigate();

  const [shops, setShops] = React.useState<Shop[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const [limit] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState('name');
  const [sortOrder, setSortOrder] = React.useState<'ASC' | 'DESC'>('ASC');

  const fetchShops = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/shops`, {
        params: {
          page,
          limit,
          sortBy,
          sortOrder,
          q: search,
        },
      });
      setShops(data.data || []);
      setPages(data.pages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchShops();
  }, [page, search, sortBy, sortOrder]);

  const handleDelete = async (shopId: number) => {
    if (!confirm('Видалити цей магазин?')) return;
    try {
      await axios.delete(`${API_URL}/shops/${shopId}`);
      fetchShops();
    } catch (err) {
      console.error(err);
    }
  };

  const [sortValue, setSortValue] = React.useState('name_asc');

  const handleSortChange = (value: string) => {
    setSortValue(value);

    switch (value) {
      case 'name_asc':
        setSortBy('name');
        setSortOrder('ASC');
        break;
      case 'name_desc':
        setSortBy('name');
        setSortOrder('DESC');
        break;
      case 'address_asc':
        setSortBy('address');
        setSortOrder('ASC');
        break;
      case 'address_desc':
        setSortBy('address');
        setSortOrder('DESC');
        break;
      case 'created_desc':
        setSortBy('created_at');
        setSortOrder('DESC');
        break;
      case 'created_asc':
        setSortBy('created_at');
        setSortOrder('ASC');
        break;
      case 'updated_desc':
        setSortBy('updated_at');
        setSortOrder('DESC');
        break;
    }

    setPage(1);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Магазини
      </Typography>

      <Button
        variant="contained"
        sx={{ mb: 2 }}
        onClick={() => navigate('/shops/new')}
      >
        Створити магазин
      </Button>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Пошук магазину..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <Select
          value={sortValue}
          onChange={(e) => handleSortChange(e.target.value)}
        >
          <MenuItem value="name_asc">Назва (А - Я)</MenuItem>
          <MenuItem value="name_desc">Назва (Я - А)</MenuItem>
          <MenuItem value="address_asc">Адреса (А - Я)</MenuItem>
          <MenuItem value="address_desc">Адреса (Я - А)</MenuItem>
          <MenuItem value="created_desc">Нові</MenuItem>
          <MenuItem value="created_asc">Старі</MenuItem>
          <MenuItem value="updated_desc">Оновлені</MenuItem>
        </Select>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Адреса</TableCell>
              <TableCell>Місто</TableCell>
              <TableCell>Години роботи</TableCell>
              <TableCell>Менеджер</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Завантаження...
                </TableCell>
              </TableRow>
            ) : shops.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Немає магазинів
                </TableCell>
              </TableRow>
            ) : (
              shops.map((shop) => (
                <TableRow key={shop.shopId}>
                  <TableCell>{shop.name}</TableCell>
                  <TableCell>{shop.address}</TableCell>
                  <TableCell>{shop.city?.name || '—'}</TableCell>
                  <TableCell>{shop.workTime || '—'}</TableCell>
                  <TableCell>{shop.manager?.name || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => navigate(`/shops/edit/${shop.shopId}`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(shop.shopId)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            page={page}
            count={pages}
            onChange={(_, v) => setPage(v)}
          />
        </Box>
      )}
    </Box>
  );
}
